import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarClock, Mail, MessageCircle, Phone, Search, Trash2 } from "lucide-react";
import { useFacility } from "../../../context/FacilityContext";
import { Avatar, PRIORITY_TONE, StatusBadge, leadStatusLabel } from "./ui";
import { ChoiceRow, Drawer, DrawerField, DrawerGroup, drawerInput } from "../Drawer";
import { PrimaryButton } from "../ui";
import { InlineLoader, PageLoader } from "../PageLoader";
import { fmtAgo, fmtDate, NO_DATA } from "../../lib/format";
import {
  PRIORITIES, PRIORITY_KEY, PriorityBadge, TASK_STATUSES, TASK_STATUS_KEY, TASK_TYPES, TASK_TYPE_KEY, TaskStatusBadge, TimeInput,
  apiCall, apiGet, currentUser, dateValue, fmtPhone, fmtTime, initialsFor, leadCode, phoneDigits, timeValue, toTask, usePeople,
} from "./contactsKit";

/**
 * New / edit task drawer (design: components/crm/TaskDrawer.tsx), on the lead
 * tasks API.
 *
 * Real data and actions: create (POST /api/leads/:leadId/tasks), edit and
 * status (PUT /api/leads/:leadId/tasks/:taskId), delete
 * (DELETE /api/leads/:leadId/tasks/:taskId), the people a task can go to
 * (GET /api/staff/assignable-users), the lead picker (GET /api/leads?search)
 * and the lead shown in the header (GET /api/leads/:id). WhatsApp is a task
 * type (task_type "whatsapp", item 159). An existing task can move to another
 * lead: the PUT goes to its current lead with the new one as "lead_id" (item 161).
 *
 * Props (design's, plus `task`):
 *   open, onClose, taskId?, leadId?, defaults?, olliPrefill?, onClearOlli?,
 *   onSaved?({ id, created, task }), onDeleted?(task)
 *   task? — the lead_tasks row (or a toTask() object) being edited. Pass it when
 *   you have it; otherwise give taskId + leadId and the drawer loads it.
 */

const TEMPLATES = [
  { title: "Call back", type: "Call" },
  { title: "Send WhatsApp reminder", type: "WhatsApp" },
  { title: "Confirm appointment", type: "Meeting" },
  { title: "Share treatment estimate", type: "Email" },
  { title: "Follow up after visit", type: "Follow-up" },
];
/** Choosing a type fills the title on a new task, unless the person has written their own. */
const isAutoTitle = (title) => { const t = title.trim(); return !t || TASK_TYPES.includes(t) || TEMPLATES.some((template) => template.title === t); };

const TYPE_ICON = { Call: Phone, WhatsApp: MessageCircle, "Follow-up": CalendarClock, Meeting: CalendarClock, Email: Mail };

const firstName = (name) => name.split(/\s+/)[0] ?? name;
const dueTimestamp = (date, time) => new Date(`${date}T${time}`).getTime();
function defaultDue() { const date = new Date(); date.setDate(date.getDate() + 1); date.setHours(10, 0, 0, 0); return date.getTime(); }
function dueSummary(timestamp) {
  const dueDay = new Date(timestamp).setHours(0, 0, 0, 0);
  const today = new Date().setHours(0, 0, 0, 0);
  const tomorrow = new Date(today + 86_400_000).setHours(0, 0, 0, 0);
  const day = dueDay === today ? "today" : dueDay === tomorrow ? "tomorrow" : fmtDate(timestamp);
  return `Due ${day} at ${fmtTime(timestamp)}`;
}
const asTask = (t) => (t && t.raw ? t : t ? toTask(t) : null);
/** leads row → what the lead picker shows. */
const toPick = (l) => ({ id: l.id, name: [l.first_name, l.last_name].filter(Boolean).join(" ") || "Unknown contact", phone: phoneDigits(l.phone), status: leadStatusLabel(l.status_key || l.status), service: l.interested_service || "" });

export function TaskDrawer({ open, onClose, taskId, leadId, defaults, olliPrefill = false, onClearOlli, onSaved, onDeleted, task: givenTask }) {
  const fac = useFacility();
  const people = usePeople();
  const me = currentUser().id;
  const [task, setTask] = useState(() => asTask(givenTask));
  const makeInitial = useCallback((existing) => {
    if (existing) {
      return { title: existing.title, type: existing.type, priority: existing.priority, dueDate: dateValue(existing.due), dueTime: existing.hasTime ? timeValue(existing.due) : "", assignee: existing.assigneeId || "", notes: existing.notes || "", status: existing.status, leadId: existing.leadId };
    }
    const due = defaults?.due ?? defaultDue();
    return { title: defaults?.title ?? "", type: defaults?.type ?? "Follow-up", priority: defaults?.priority ?? "Medium", dueDate: dateValue(due), dueTime: timeValue(due), assignee: defaults?.assignee ?? me ?? "", notes: defaults?.notes ?? "", status: defaults?.status ?? "To do", leadId: leadId ?? "" };
  }, [defaults, leadId, me]);
  const [form, setForm] = useState(() => makeInitial(null));
  const [initial, setInitial] = useState(() => makeInitial(null));
  const [leadQuery, setLeadQuery] = useState("");
  const [leadSearchOpen, setLeadSearchOpen] = useState(false);
  const [leadMatches, setLeadMatches] = useState([]);
  const [matchesFor, setMatchesFor] = useState(null); // the search leadMatches answer
  const [selectedLead, setSelectedLead] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const notesRef = useRef(null);
  const facilityId = fac.apiParams().facility_id;
  // Which task the form was filled for: until the one asked for is in the form
  // (fetched when only its id was given), the drawer shows a loader rather than
  // a blank form or the previous task's values.
  const formKey = `${givenTask?.id ?? taskId ?? "new"}|${leadId ?? ""}`;
  const [formFor, setFormFor] = useState(null);
  const formLoading = open && formFor !== formKey;

  // Load the task (when only its id was given) and reset the form each time the drawer opens.
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    const key = formKey;
    const start = (existing) => {
      if (!live) return;
      setFormFor(key);
      setTask(existing);
      const next = makeInitial(existing);
      setForm(next);
      setInitial(next);
      setErrors({});
      setLeadSearchOpen(false);
      setLeadQuery("");
      setSelectedLead(null);
    };
    const given = asTask(givenTask);
    if (given) start(given);
    else if (taskId && leadId) apiGet(`/api/leads/${leadId}/tasks`).then((r) => start(asTask((r?.data || []).find((t) => String(t.id) === String(taskId)))));
    else start(null);
    return () => { live = false; };
  }, [open, taskId, leadId, givenTask, makeInitial]); // eslint-disable-line react-hooks/exhaustive-deps

  // The lead this task is for, for the header and the lead card.
  useEffect(() => {
    if (!open || !form.leadId) { setSelectedLead(null); return undefined; }
    let live = true;
    apiGet(`/api/leads/${form.leadId}`).then((r) => {
      if (!live || !r?.success) return;
      const pick = toPick(r.data);
      setSelectedLead(pick);
      setLeadQuery(`${leadCode(pick.id)} · ${pick.name}`);
    });
    return () => { live = false; };
  }, [open, form.leadId]);

  // Lead search
  const matchKey = `${leadQuery.trim().replace(/^l-/i, "")}|${facilityId ?? ""}`;
  useEffect(() => {
    if (!open || !leadSearchOpen) return undefined;
    let live = true;
    const q = leadQuery.trim().replace(/^l-/i, "");
    const t = setTimeout(() => {
      apiGet("/api/leads", { limit: 8, ...(facilityId ? { facility_id: facilityId } : {}), ...(q ? { search: q } : {}) }).then((r) => { if (live) { setLeadMatches((r?.data || []).map(toPick)); setMatchesFor(`${q}|${facilityId ?? ""}`); } });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [open, leadSearchOpen, leadQuery, facilityId]);

  useEffect(() => {
    const textarea = notesRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [form.notes, open]);

  const dirty = Object.keys(form).some((key) => form[key] !== initial[key]);
  const due = dueTimestamp(form.dueDate, form.dueTime || "23:59");
  const dueValid = Number.isFinite(due) && !!form.dueTime;
  const overdue = dueValid && due < Date.now();
  const assignees = useMemo(() => {
    const list = people.map((p) => ({ id: p.id, name: p.name }));
    if (task?.assigneeId && !list.some((p) => p.id === task.assigneeId)) list.push({ id: task.assigneeId, name: task.assignee || "Previous assignee" });
    return list;
  }, [people, task]);

  function setQuickDue(kind) {
    const date = new Date();
    if (kind === "hour") date.setHours(date.getHours() + 1, 0, 0, 0);
    if (kind === "today") date.setHours(18, 0, 0, 0);
    if (kind === "tomorrow") date.setDate(date.getDate() + 1);
    if (kind === "monday") date.setDate(date.getDate() + ((8 - date.getDay()) % 7 || 7));
    if (kind === "tomorrow" || kind === "monday") date.setHours(10, 0, 0, 0);
    setForm((current) => ({ ...current, dueDate: dateValue(date.getTime()), dueTime: timeValue(date.getTime()) }));
  }

  async function save() {
    if (saving) return;
    const title = form.title.trim();
    const nextErrors = {
      ...(title ? {} : { title: "Add a title so the team knows what to do." }),
      ...(!form.leadId ? { lead: "Pick the lead this task is for." } : {}),
      ...(!dueValid ? { due: "Choose a valid due date and time." } : {}),
    };
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    const body = {
      title,
      task_type: task && form.type === task.type ? task.typeKey : TASK_TYPE_KEY[form.type],
      priority: task && form.priority === task.priority ? task.priorityKey : PRIORITY_KEY[form.priority],
      due_date: form.dueDate,
      due_time: `${form.dueTime}:00`,
      assigned_to: form.assignee || null,
      description: form.notes.trim(),
    };
    setSaving(true);
    try {
      if (task) {
        const moved = String(form.leadId) !== String(task.leadId);
        const r = await apiCall("put", `/api/leads/${task.leadId}/tasks/${task.id}`, { ...body, status: TASK_STATUS_KEY[form.status], ...(moved ? { lead_id: Number(form.leadId) } : {}) });
        onSaved?.({ id: task.id, created: false, task: r?.data ? toTask(r.data) : null });
      } else {
        const r = await apiCall("post", `/api/leads/${form.leadId}/tasks`, body);
        onSaved?.({ id: r?.data?.id, created: true, task: r?.data ? toTask(r.data) : null });
      }
      setSaving(false);
      onClose();
    } catch (e) {
      setSaving(false);
      setErrors({ save: e.message });
    }
  }

  async function remove() {
    if (!task) return;
    try {
      await apiCall("delete", `/api/leads/${task.leadId}/tasks/${task.id}`);
      onDeleted?.(task);
      onClose();
    } catch (e) {
      setErrors({ save: e.message });
    }
  }

  const saveRef = useRef(save);
  saveRef.current = formLoading ? () => {} : save;
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        saveRef.current();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open]);

  const context = selectedLead
    ? `${leadCode(selectedLead.id)} · ${selectedLead.name}${selectedLead.phone ? ` · ${fmtPhone(selectedLead.phone)}` : ""}`
    : leadId ? leadCode(leadId) : "Task details";
  const priorityRender = (option, selected) => <PriorityBadge priority={option} inverted={selected} />;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={formLoading ? (givenTask?.id ?? taskId ? "Edit task" : "New task") : task ? "Edit task" : "New task"}
      context={formLoading ? (leadId ? leadCode(leadId) : "Task details") : context}
      width="lg"
      dirty={dirty}
      danger={task && !formLoading ? <button type="button" onClick={remove} className="flex h-10 items-center gap-2 rounded-full px-3 text-small font-semibold text-danger hover:bg-danger-soft"><Trash2 size={15} strokeWidth={1.75} />Delete task</button> : undefined}
      actions={formLoading ? undefined : <PrimaryButton type="button" onClick={save} loading={saving} loadingLabel="Saving…" className="h-11 px-5 text-small">{task ? "Save changes" : "Add task"}</PrimaryButton>}
    >
      {formLoading ? <PageLoader label="Loading task…" className="min-h-[40vh]" /> : <div className="flex flex-col gap-6">
        {olliPrefill && <div className="flex items-center justify-between gap-3 rounded-md bg-sky-soft px-3 py-2 text-small text-ink"><span>Filled by Olli. Check the details before you save.</span><button type="button" onClick={onClearOlli} className="shrink-0 text-small font-semibold text-sky-deep">Clear</button></div>}
        {task && <p className="-mt-2 flex flex-wrap items-center gap-2 text-caption text-muted">Created {task.createdAt ? fmtAgo(task.createdAt) : NO_DATA} by {task.createdBy || NO_DATA}<span aria-hidden>·</span><TaskStatusBadge status={form.status} /></p>}
        <DrawerGroup>
          <input
            aria-label="Task title"
            data-autofocus
            value={form.title}
            onChange={(event) => { setForm((current) => ({ ...current, title: event.target.value })); setErrors((current) => ({ ...current, title: undefined })); }}
            placeholder="What needs to happen?"
            className="w-full border-0 border-b border-line bg-transparent px-0 pb-3 text-h3 font-semibold text-ink outline-none placeholder:text-muted/60 focus:border-ink"
          />
          {errors.title && <p role="alert" className="text-caption text-danger">{errors.title}</p>}
          {!task && !form.title && <div className="flex flex-wrap gap-2">{TEMPLATES.map((template) => <button key={template.title} type="button" onClick={() => setForm((current) => ({ ...current, title: template.title, type: template.type }))} className="rounded-full bg-mist px-3 py-1.5 text-caption font-medium text-ink hover:bg-line">{template.title}</button>)}</div>}
        </DrawerGroup>

        <DrawerGroup title="Type">
          <ChoiceRow value={form.type} options={TASK_TYPES} onChange={(value) => { setForm((current) => ({ ...current, type: value, title: !task && isAutoTitle(current.title) ? value : current.title })); setErrors((current) => ({ ...current, type: undefined, title: undefined })); }} label="Task type" render={(option) => { const Icon = TYPE_ICON[option]; return <><Icon size={14} strokeWidth={1.75} />{option}</>; }} />
          {errors.type && <p role="alert" className="text-caption text-danger">{errors.type}</p>}
        </DrawerGroup>

        <DrawerGroup title="When">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setQuickDue("hour")} className="rounded-full bg-mist px-3 py-1.5 text-caption font-medium hover:bg-line">In 1 hour</button>
            <button type="button" onClick={() => setQuickDue("today")} className="rounded-full bg-mist px-3 py-1.5 text-caption font-medium hover:bg-line">Today 6 PM</button>
            <button type="button" onClick={() => setQuickDue("tomorrow")} className="rounded-full bg-mist px-3 py-1.5 text-caption font-medium hover:bg-line">Tomorrow 10 AM</button>
            <button type="button" onClick={() => setQuickDue("monday")} className="rounded-full bg-mist px-3 py-1.5 text-caption font-medium hover:bg-line">Next Monday 10 AM</button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <DrawerField label="Date" htmlFor="task-due-date"><input id="task-due-date" type="date" className={drawerInput} value={form.dueDate} onChange={(event) => { setForm((current) => ({ ...current, dueDate: event.target.value })); setErrors((current) => ({ ...current, due: undefined })); }} /></DrawerField>
            <DrawerField label="Time" htmlFor="task-due-time"><TimeInput id="task-due-time" size="sm" value={form.dueTime} onChange={(value) => { setForm((current) => ({ ...current, dueTime: value })); setErrors((current) => ({ ...current, due: undefined })); }} /></DrawerField>
          </div>
          {dueValid && <p className={`text-caption ${overdue ? "text-danger" : "text-muted"}`}>{overdue ? "This time has passed. Pick a later time." : dueSummary(due)}</p>}
          {errors.due && <p role="alert" className="text-caption text-danger">{errors.due}</p>}
        </DrawerGroup>

        <DrawerGroup>
          <DrawerField label="Priority">
            <ChoiceRow value={form.priority} options={PRIORITIES} onChange={(value) => setForm((current) => ({ ...current, priority: value }))} label="Priority" render={priorityRender} selectedClass={(option) => PRIORITY_TONE[option]?.bar} />
          </DrawerField>
          <DrawerField label="Assigned to">
            <div role="radiogroup" aria-label="Assigned to" className="flex flex-wrap gap-2">
              {assignees.map((person) => {
                const selected = String(form.assignee) === String(person.id);
                return <button key={person.id} type="button" role="radio" aria-checked={selected} onClick={() => setForm((current) => ({ ...current, assignee: person.id }))} className={`flex h-10 items-center gap-2 rounded-full px-2.5 pr-3 text-caption font-medium ring-1 ${selected ? "bg-mist text-ink ring-ink" : "bg-paper text-muted ring-line hover:text-ink"}`}><Avatar name={person.name} initials={initialsFor(person.name)} size="sm" />{firstName(person.name)}</button>;
              })}
            </div>
          </DrawerField>
        </DrawerGroup>

        {/* Shown for a new task without a fixed lead, and for any existing task so it can be moved (item 161). */}
        {(!leadId || task) && <DrawerGroup title="Lead">
          {selectedLead && !leadSearchOpen ? <div className="flex items-center gap-3 rounded-sm border border-line bg-mist/50 p-3">
            <Avatar name={selectedLead.name} initials={initialsFor(selectedLead.name)} size="md" />
            <div className="min-w-0 flex-1"><p className="truncate text-small font-semibold">{selectedLead.name}</p><p className="mt-0.5 flex items-center gap-2 text-caption text-muted">{leadCode(selectedLead.id)}<StatusBadge status={selectedLead.status} /></p></div>
            <button type="button" onClick={() => { setLeadSearchOpen(true); setLeadQuery(""); }} className="text-caption font-semibold text-muted hover:text-ink">Change</button>
          </div> : <div className="relative">
            <div className="relative"><Search size={15} strokeWidth={1.75} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" /><input value={leadQuery} onFocus={() => setLeadSearchOpen(true)} onChange={(event) => { setLeadQuery(event.target.value); setLeadSearchOpen(true); setForm((current) => ({ ...current, leadId: "" })); }} placeholder="Search name, phone or lead ID" className={`${drawerInput} pl-9`} autoComplete="off" /></div>
            {leadSearchOpen && <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 max-h-56 overflow-y-auto rounded-sm bg-paper p-1.5 shadow-lift ring-1 ring-line">
              {matchesFor !== matchKey ? <div className="flex justify-center px-3 py-3"><InlineLoader /></div> : leadMatches.length ? leadMatches.map((lead) => (
                <button key={lead.id} type="button" onClick={() => { setForm((current) => ({ ...current, leadId: lead.id })); setSelectedLead(lead); setLeadQuery(`${leadCode(lead.id)} · ${lead.name}`); setLeadSearchOpen(false); setErrors((current) => ({ ...current, lead: undefined })); }} className="flex w-full items-center gap-3 rounded-xs px-2.5 py-2 text-left hover:bg-mist">
                  <Avatar name={lead.name} initials={initialsFor(lead.name)} size="sm" /><span className="min-w-0 flex-1"><span className="block truncate text-small font-medium">{lead.name}</span><span className="block text-caption text-muted">{leadCode(lead.id)}{lead.phone ? ` · ${fmtPhone(lead.phone)}` : ""}</span></span><span className="text-caption text-muted">{lead.status}</span>
                </button>
              )) : <p className="px-3 py-3 text-small text-muted">No leads found.</p>}
            </div>}
            {errors.lead && <p role="alert" className="mt-1.5 text-caption text-danger">{errors.lead}</p>}
          </div>}
          {selectedLead && <p className="text-caption text-muted">{selectedLead.phone ? fmtPhone(selectedLead.phone) : selectedLead.service}</p>}
        </DrawerGroup>}

        {task && <DrawerGroup title="Status"><ChoiceRow value={form.status} options={TASK_STATUSES} onChange={(value) => setForm((current) => ({ ...current, status: value }))} label="Task status" /></DrawerGroup>}

        <DrawerGroup title="Notes" hint="Add context that will help the team take the next step.">
          <DrawerField label="Notes" htmlFor="task-notes" optional><textarea ref={notesRef} id="task-notes" rows={1} value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} placeholder="Add context for the team" className="min-h-24 w-full resize-none overflow-hidden rounded-sm border border-line bg-paper px-3.5 py-3 text-small text-ink outline-none placeholder:text-muted/70 focus:border-ink focus:ring-2 focus:ring-ink/10" /></DrawerField>
        </DrawerGroup>
        {errors.save && <p role="alert" className="text-caption text-danger">{errors.save}</p>}
        <p className="-mt-2 text-caption text-muted">Press ⌘/Ctrl + Enter to save.</p>
      </div>}
    </Drawer>
  );
}

export default TaskDrawer;
